/**
 * XState machine for PRD Generation session lifecycle.
 *
 * Replaces complex useEffect hooks and ref-based guards with explicit state transitions.
 * Handles both session initialization and PRD generation progress.
 *
 * States:
 * - idle: No session, waiting for user to start
 * - hydrating: Loading from localStorage
 * - loadingExisting: Loading existing session from API
 * - startingNew: Creating new session via API
 * - generating: PRD generation in progress (showing phase progress)
 * - readyForFinalization: PRD complete, ready for review
 * - completed: PRD finalized (project has existingPRD)
 * - error: Error occurred
 *
 * Events:
 * - START_SESSION: User clicked "Start PRD Generation"
 * - HYDRATION_COMPLETE: localStorage check done
 * - EXISTING_SESSION_FOUND: Found session in store
 * - EXISTING_PRD_FOUND: Project already has PRD
 * - SESSION_LOADED: Existing session loaded successfully
 * - SESSION_STARTED: New session created successfully
 * - GENERATION_COMPLETE: SSE event - PRD generation done
 * - ERROR: Error occurred
 * - RETRY: Retry after error
 * - START_OVER: Cancel and start fresh
 * - CANCEL: Cancel session
 * - PROJECT_CHANGED: Project ID changed (reset machine)
 */

import { assign, setup } from 'xstate';

import type { PRDSessionState } from '~/types';

/**
 * Machine events - all possible state transitions.
 */
export type PRDSessionEvent =
  | { type: 'START_SESSION' }
  | { activeSessionId: string | null; hasExistingPRD: boolean; type: 'HYDRATION_COMPLETE' }
  | { sessionId: string; type: 'EXISTING_SESSION_FOUND' }
  | { type: 'EXISTING_PRD_FOUND' }
  | {
      classificationConfidence: string | null;
      classificationReasoning: string | null;
      completenessScore: number;
      generationPhase: string | null;
      prdPreview: string;
      projectSize: string | null;
      scenarioType: string | null;
      sessionId: string;
      sessionState: PRDSessionState;
      status: string;
      type: 'SESSION_LOADED';
    }
  | {
      classificationConfidence: string | null;
      classificationReasoning: string | null;
      completenessScore: number;
      generationPhase: string | null;
      isResumed: boolean;
      prdPreview: string;
      projectSize: string | null;
      scenarioType: string | null;
      sessionId: string;
      sessionState: PRDSessionState;
      type: 'SESSION_STARTED';
    }
  | {
      completenessScore: number;
      prdPreview: string;
      sessionState: PRDSessionState;
      type: 'GENERATION_COMPLETE';
    }
  | { conflictOwner?: { displayName: string; userId: string }; error: string; type: 'ERROR' }
  | { type: 'RETRY' }
  | { type: 'START_OVER' }
  | { type: 'CANCEL' }
  | { projectId: string; projectName: string; type: 'PROJECT_CHANGED' };

/**
 * Machine context - all data needed for PRD session management.
 */
export interface PRDSessionContext {
  // Session identifiers
  sessionId: string | null;
  projectId: string;
  projectName: string;

  // Backend session state (single source of truth)
  sessionState: PRDSessionState | null;

  // PRD data
  completenessScore: number;
  prdPreview: string;

  // Classification data (validated before setting)
  scenarioType: string | null;
  projectSize: string | null;
  classificationConfidence: string | null;
  classificationReasoning: string | null;

  // Error state
  error: string | null;
  conflictOwner: {
    displayName: string;
    userId: string;
  } | null;

  // Flags
  hasExistingPRD: boolean; // Project already has PRD
  activeSessionId: string | null; // Session ID from store
  userRequestedStart: boolean; // User explicitly clicked "Start"
  generationPhase: string | null; // Backend generation phase for resume
}

/**
 * PRD Session input for initialization.
 */
export interface PRDSessionInput {
  projectId: string;
  projectName: string;
}

/**
 * PRD Session state machine.
 *
 * Manages the entire lifecycle from initialization through completion.
 */
export const prdSessionMachine = setup({
  types: {
    context: {} as PRDSessionContext,
    events: {} as PRDSessionEvent,
    input: {} as PRDSessionInput,
  },
  actions: {
    // Action: Set user requested start flag
    setUserRequestedStart: assign({
      userRequestedStart: true,
    }),

    // Action: Update context with loaded session data
    setSessionData: assign({
      sessionId: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.sessionId
          : null,
      sessionState: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.sessionState
          : null,
      completenessScore: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.completenessScore
          : 0,
      prdPreview: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED' ? event.prdPreview : '',
      scenarioType: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.scenarioType
          : null,
      projectSize: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.projectSize
          : null,
      classificationConfidence: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.classificationConfidence
          : null,
      classificationReasoning: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.classificationReasoning
          : null,
      generationPhase: ({ event }) =>
        event.type === 'SESSION_LOADED' || event.type === 'SESSION_STARTED'
          ? event.generationPhase
          : null,
      error: null,
      conflictOwner: null,
    }),

    // Action: Update generation completion data
    setGenerationComplete: assign({
      sessionState: ({ event }) =>
        event.type === 'GENERATION_COMPLETE' ? event.sessionState : null,
      completenessScore: ({ event }) =>
        event.type === 'GENERATION_COMPLETE' ? event.completenessScore : 0,
      prdPreview: ({ event }) => (event.type === 'GENERATION_COMPLETE' ? event.prdPreview : ''),
    }),

    // Action: Set error state
    setError: assign({
      error: ({ event }) => (event.type === 'ERROR' ? event.error : null),
      conflictOwner: ({ event }) =>
        event.type === 'ERROR' && event.conflictOwner ? event.conflictOwner : null,
    }),

    // Action: Clear error state
    clearError: assign({
      error: null,
      conflictOwner: null,
    }),

    // Action: Reset session state
    resetSession: assign({
      sessionId: null,
      sessionState: null,
      completenessScore: 0,
      prdPreview: '',
      scenarioType: null,
      projectSize: null,
      classificationConfidence: null,
      classificationReasoning: null,
      error: null,
      conflictOwner: null,
      userRequestedStart: false,
      generationPhase: null,
    }),

    // Action: Reset for project change
    resetForProjectChange: assign({
      projectId: ({ event }) => (event.type === 'PROJECT_CHANGED' ? event.projectId : ''),
      projectName: ({ event }) => (event.type === 'PROJECT_CHANGED' ? event.projectName : ''),
      sessionId: null,
      sessionState: null,
      completenessScore: 0,
      prdPreview: '',
      scenarioType: null,
      projectSize: null,
      classificationConfidence: null,
      classificationReasoning: null,
      error: null,
      conflictOwner: null,
      userRequestedStart: false,
      hasExistingPRD: false,
      activeSessionId: null,
      generationPhase: null,
    }),

    // Action: Update hydration results
    setHydrationResults: assign({
      activeSessionId: ({ event }) =>
        event.type === 'HYDRATION_COMPLETE' ? event.activeSessionId : null,
      hasExistingPRD: ({ event }) =>
        event.type === 'HYDRATION_COMPLETE' ? event.hasExistingPRD : false,
    }),
  },
  guards: {
    // Guard: Has active session from store
    hasActiveSession: ({ context }) => context.activeSessionId !== null,

    // Guard: Has existing PRD
    hasExistingPRD: ({ context }) => context.hasExistingPRD,

    // Guard: User requested to start
    userRequestedStart: ({ context }) => context.userRequestedStart,

    // Guard: Session is generating (for resume)
    isGenerating: ({ context }) =>
      context.generationPhase !== null && context.generationPhase !== 'completed',

    // Guard: Session failed
    sessionFailed: ({ event }) => event.type === 'SESSION_LOADED' && event.status === 'failed',
  },
}).createMachine({
  id: 'prdSession',
  initial: 'hydrating',
  context: ({ input }) => ({
    sessionId: null,
    projectId: input.projectId,
    projectName: input.projectName,
    sessionState: null,
    completenessScore: 0,
    prdPreview: '',
    scenarioType: null,
    projectSize: null,
    classificationConfidence: null,
    classificationReasoning: null,
    error: null,
    conflictOwner: null,
    hasExistingPRD: false,
    activeSessionId: null,
    userRequestedStart: false,
    generationPhase: null,
  }),
  states: {
    // Hydrating: Check localStorage for existing session/PRD
    hydrating: {
      on: {
        HYDRATION_COMPLETE: {
          actions: ['setHydrationResults'],
          target: 'checkingState',
        },
      },
    },

    // Checking state: Decide next action based on hydration results
    checkingState: {
      always: [
        {
          guard: 'hasExistingPRD',
          target: 'completed',
        },
        {
          guard: 'hasActiveSession',
          target: 'loadingExisting',
        },
        {
          target: 'idle',
        },
      ],
    },

    // Idle: No session, waiting for user to start
    idle: {
      on: {
        START_SESSION: {
          actions: ['setUserRequestedStart'],
          target: 'startingNew',
        },
        EXISTING_PRD_FOUND: {
          target: 'completed',
        },
        PROJECT_CHANGED: {
          actions: ['resetForProjectChange'],
          target: 'hydrating',
        },
      },
    },

    // Loading existing: Fetch session from API
    loadingExisting: {
      on: {
        SESSION_LOADED: [
          {
            guard: 'sessionFailed',
            target: 'error',
            actions: ['setError'],
          },
          {
            guard: 'isGenerating',
            target: 'generating',
            actions: ['setSessionData'],
          },
          {
            target: 'readyForFinalization',
            actions: ['setSessionData'],
          },
        ],
        ERROR: {
          target: 'error',
          actions: ['setError'],
        },
      },
    },

    // Starting new: Create session via API
    startingNew: {
      on: {
        SESSION_STARTED: [
          {
            guard: 'isGenerating',
            target: 'generating',
            actions: ['setSessionData'],
          },
          {
            target: 'readyForFinalization',
            actions: ['setSessionData'],
          },
        ],
        ERROR: {
          target: 'error',
          actions: ['setError'],
        },
      },
    },

    // Generating: PRD generation in progress
    generating: {
      on: {
        GENERATION_COMPLETE: {
          target: 'readyForFinalization',
          actions: ['setGenerationComplete'],
        },
        ERROR: {
          target: 'error',
          actions: ['setError'],
        },
        CANCEL: {
          target: 'idle',
          actions: ['resetSession'],
        },
      },
    },

    // Ready for finalization: PRD complete, showing review button
    readyForFinalization: {
      on: {
        START_OVER: {
          target: 'idle',
          actions: ['resetSession'],
        },
        CANCEL: {
          target: 'idle',
          actions: ['resetSession'],
        },
        PROJECT_CHANGED: {
          actions: ['resetForProjectChange'],
          target: 'hydrating',
        },
      },
    },

    // Completed: PRD finalized (project has existingPRD)
    completed: {
      on: {
        PROJECT_CHANGED: {
          actions: ['resetForProjectChange'],
          target: 'hydrating',
        },
      },
    },

    // Error: Something went wrong
    error: {
      on: {
        RETRY: {
          target: 'startingNew',
          actions: ['clearError', 'setUserRequestedStart'],
        },
        START_OVER: {
          target: 'idle',
          actions: ['resetSession'],
        },
        PROJECT_CHANGED: {
          actions: ['resetForProjectChange'],
          target: 'hydrating',
        },
      },
    },
  },
});
